import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { DSSetup, Service } from 'webapp-types';
import type { BoardCustomContainer } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getFormattedTime } from 'webapp/utils/number';

import type Board from 'webapp/models-custom/board';
import type BoardState from 'webapp/models-custom/board-state';
import type DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';

import type Account from 'webapp/services/account';
import type Billing from 'webapp/services/billing';

export interface BoardBaseArgs {
	boardState: BoardState;
	isInDesigner?: boolean;
	inCustomContainer?: BoardCustomContainer;
	isPublicUrl?: boolean;
	isActive?: boolean;
	showBottomInfo?: boolean;
	skipActions?: boolean;
	slideCount?: number;
	onSave?: () => void;
	onDelete?: () => void;
	onCopy?: () => void;
	trackingOrigin?: string;
}

export default class BoardBaseComponent<T extends BoardBaseArgs> extends Component<T> {
	@service declare account: Account;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service declare billing: Billing;
	@service declare analytics: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	el!: JQuery;

	get model() {
		return this.args.boardState?.board as Board;
	}

	get isViewOnly(): boolean {
		return !this.args.isInDesigner;
	}

	get isActive(): boolean {
		return this.args.isActive ?? true;
	}

	get isFromReport(): boolean {
		return this.args.trackingOrigin === 'report_editor';
	}

	get trackingOriginForMaster(): string {
		return this.isFromReport ? 'master_editor' : 'master_designer';
	}

	get isInMetricInsight() {
		return this.metricDetailsService.isMetricInsightsTemporaryId(
			this.model.numericId?.toString(),
		);
	}

	get isInGoalInsight() {
		return this.mmGoalsService.isTemporaryFakeId(this.model.numericId?.toString());
	}

	get showBottomInfo(): boolean {
		return (
			(this.args.showBottomInfo ?? true) && !this.isInMetricInsight && !this.isInGoalInsight
		);
	}

	get canEditName(): boolean {
		return !!this.args.isInDesigner;
	}

	get hideBranding(): boolean {
		return this.account.removeBrandingOnFeatures;
	}

	get isExpiredSpace(): boolean {
		return (
			this.isViewOnly &&
			!this.showLoader &&
			(this.navigationService.isDatawallAndExpired || this.billing.isExpired)
		);
	}

	get isInactiveSpace(): boolean {
		return this.isViewOnly && !this.showLoader && this.navigationService.isDatawallAndInactive;
	}

	get isSampleData(): boolean {
		return this.isViewOnly && !this.showLoader && this.model.isRandom;
	}

	get gradientStyle() {
		const style = this.model.colorGradient;
		return htmlSafe(style);
	}

	get showLoader(): boolean {
		return (
			!this.model ||
			this.model.isDataLoading ||
			// || !this.navigationService.isFontLoaded
			(!this.isActive && !this.navigationService.isLoadedFromSnapshooter)
		);
	}

	get showBoardFetchLoader(): boolean {
		return this.isViewOnly && !this.showLoader && this.args.boardState.isLoadingData;
	}

	get showHelpArticles(): boolean {
		return !this.args.skipActions && !this.account.hideHelpArticles;
	}

	get showResetPreviewToOriginal() {
		return (
			this.isViewOnly &&
			this.args.boardState.board.isPreview &&
			(this.showDateRangeMasterSwitcher || this.showFilterMasterSwitcher)
		);
	}

	get canHaveMasterSwitcher() {
		return (
			!this.navigationService.isLoadedFromSnapshooter && !this.showLoader && !!this.model.id
		);
	}

	get showDateRangeMasterSwitcher(): boolean {
		return (
			this.canHaveMasterSwitcher &&
			(this.args.isInDesigner || this.args.boardState.allowDateRangePreviews)
		);
	}

	get showFilterMasterSwitcher(): boolean {
		const { boardCM, isViewOnly, isReportEditor, allowFilterPreviews } = this.args.boardState;
		const isViewOnlyRandomizedBoard = boardCM.isRandom && isViewOnly && !isReportEditor; // account and public templates

		return (
			this.canHaveMasterSwitcher &&
			!isViewOnlyRandomizedBoard &&
			(this.args.isInDesigner || allowFilterPreviews)
		);
	}

	get showLoadingInsteadOfLastSync(): boolean {
		return this.model.isDataIncomplete || !!this.args.boardState?.isBlocksLoadingData;
	}

	get lastSyncText(): string {
		let lastSyncText = this.model.lastSyncText;
		if (this.navigationService.isLoadedFromSnapshooter) {
			lastSyncText = getFormattedTime(this.model.lastSync, 'UTC', 'MMM D, YYYY [at] hh:mm A');
		} else if (lastSyncText.includes('minutes')) {
			lastSyncText = lastSyncText.replace('minutes', 'min');
		}
		return lastSyncText;
	}

	get theme() {
		return this.args.boardState.boardCM.bgTheme;
	}

	@action
	setupView(node: HTMLElement): void {
		this.el = $(node);

		this.checkIfBoardLoaded();

		this.el.find('.view-board-inner').on('scroll.popupTooltip', () => {
			$(window).trigger('innerScroll.popupTooltip');
		});
	}

	@action
	masterDateRangeChanged(
		dateRange: DateRange | null,
		compare: DsCompare | null,
		_applyToWholeLoop?: boolean,
	): void {
		// in theory, handles also that dateRange can be reset (null),
		// but in practice, currently that is handled with `resetPreviewToOriginal` action
		const commonDateRange = this.args.boardState.commonMasterDateRange!;

		if (
			!commonDateRange ||
			!dateRange ||
			!commonDateRange.isEqual(dateRange.formula) ||
			compare?.formula
		) {
			const currentDateRange = this.args.boardState.selectedMasterDateRange;
			const currentCompare = this.args.boardState.selectedMasterCompare;
			if (
				(currentDateRange && !dateRange) ||
				(!currentDateRange && dateRange) ||
				(dateRange && currentDateRange && !currentDateRange.isEqual(dateRange.formula)) ||
				(compare && !currentCompare?.isEqual(compare?.formula ?? null))
			) {
				const newDateRange = dateRange ? dateRange.createCopy() : null;
				const newCompare = compare ? compare.createCopy() : null;
				this.args.boardState.updateMasterDateRangeSettings(newDateRange, newCompare);

				const dateRangeFormula = dateRange?.formula;
				if (
					currentDateRange &&
					(!dateRangeFormula || !currentDateRange.isGranulationEqual(dateRangeFormula))
				) {
					this.analytics.event(TrackingEvent.GRANULATION_CHANGED, { origin: 'master' });
				}
				if (
					currentDateRange &&
					(!dateRangeFormula || !currentDateRange.isRangeEqual(dateRangeFormula))
				) {
					this.analytics.event(TrackingEvent.DATE_RANGE_CHANGED, {
						origin: this.trackingOriginForMaster,
					});
				}
			}
		}
	}

	// dimensions null means reset, filters don't matter
	changeMasterFilter(dimensions: null, filters?: undefined): void;

	changeMasterFilter(
		dimensions: string[] | undefined,
		filters?: DSSetup.MetricSetupFiltersData | null,
	): void;

	@action
	changeMasterFilter(
		dimensions: string[] | null | undefined,
		filters?: DSSetup.MetricSetupFiltersData | null,
	): void {
		// dimensions null means reset
		if (dimensions === null) {
			this.args.boardState.updateMasterMetricSetupSettings(null, null);
		} else {
			this.args.boardState.updateMasterMetricSetupSettings(dimensions, filters || null);
		}
	}

	@action
	resetPreviewToOriginal(): void {
		this.args.boardState.resetToOriginal();
		this.analytics.event(TrackingEvent.DATE_RANGE_CHANGED, {
			origin: this.trackingOriginForMaster,
		});
	}

	@action
	checkIfBoardLoaded(): void {
		if (!this.model.isDataLoading) {
			scheduleOnce('afterRender', this, this.triggerBoardLoaded);
		}
	}

	triggerBoardLoaded(): void {
		this.eventManagerService.trigger(AppEvent.DESIGNER_BOARD_LOADED, {
			board: this.model,
		});
	}

	// ----- Name edit START -----
	@tracked isEditingName = false;
	@tracked editedName = '';

	@action
	startBoardNameEdit(): void {
		this.editedName = this.model.name;
		this.isEditingName = true;
	}

	@action
	endBoardNameEdit(): void {
		const editedName = this.editedName.trim();
		if (editedName) {
			this.model.name = editedName;
			this.saveBoard();
		}
		this.isEditingName = false;
	}
	// ----- Name edit END -----

	@action
	saveBoard(): void {
		if (this.args.onSave) {
			this.args.onSave();
		}
	}

	@action
	deleteBoard(): void {
		if (this.args.onDelete) {
			this.args.onDelete();
		}
	}

	@action
	copyBoard(): void {
		if (this.args.onCopy) {
			this.args.onCopy();
		}
	}
}
